import api from '@/lib/axios'

// ── Documents ─────────────────────────────────────────────────────────────────
// Espace GED : « Mes documents » (personnel) ou « Documents partagés » (toute l'entité)
export type DocumentSpace = 'mine' | 'shared'

export interface DocumentFolder { id: string; name: string; parentId?: string; color?: string; ownerId?: string; isShared?: boolean; _count?: { documents: number } }
export interface Document {
  id: string; name: string; description?: string; mimeType: string; fileSize: number
  version: number; isLatest: boolean; isArchived: boolean; isShared?: boolean; tags: string[]
  uploadedById?: string
  uploadedBy: { id: string; firstName: string; lastName: string; avatarUrl?: string | null }
  folder?: { id: string; name: string }
  expiresAt?: string; createdAt: string; updatedAt: string
  _count?: { comments: number; signatures: number }
  // Renvoyés par GET /documents/:id
  presignedUrl?: string
  comments?: DocumentComment[]
}

export interface DocumentComment {
  id: string; documentId: string; content: string; pageNumber?: number | null
  authorId: string; author: { id: string; firstName: string; lastName: string; avatarUrl?: string | null }
  resolvedAt?: string | null; editedAt?: string | null; createdAt: string
}

export interface DocumentVersion {
  id: string; version: number; fileName: string; fileSize: number
  uploadedById: string; isLatest: boolean; createdAt: string
}

/** inline : affichage dans le navigateur (aperçu) ; attachment : téléchargement. */
export type FileDisposition = 'inline' | 'attachment'

/** Réponse de GET /documents/:id/download (URL présignée, 1 h). */
export interface DocumentDownloadUrl {
  url: string; expiresIn: number; fileName: string; mimeType: string
  /** Disposition réellement accordée : 'attachment' forcé si le type n'est pas affichable en ligne. */
  disposition: FileDisposition
}

export const documentsApi = {
  getFolders: (params?: { parentId?: string; space?: DocumentSpace }) =>
    api.get('/documents/folders', { params }).then(r => r.data.data),
  createFolder: (data: { name: string; parentId?: string; color?: string; isShared?: boolean }) =>
    api.post('/documents/folders', data).then(r => r.data.data),
  getDocuments: (params?: { folderId?: string; search?: string; page?: number; limit?: number; space?: DocumentSpace }) =>
    api.get('/documents', { params }).then(r => r.data.data),
  // FIX : le backend enveloppe la réponse ({ success, data }) — renvoyer data ({ data: [], meta })
  fullTextSearch: (q: string, page = 1, limit = 20, space: DocumentSpace = 'mine') =>
    api.get('/documents/search/fulltext', { params: { q, page, limit, space } }).then(r => r.data.data),
  upload: (formData: FormData, folderId?: string, space: DocumentSpace = 'mine') => {
    if (folderId) formData.append('folderId', folderId)
    // Sans dossier : l'espace cible est explicite (avec dossier, le dossier fait foi côté backend)
    formData.append('isShared', space === 'shared' ? 'true' : 'false')
    return api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data.data)
  },
  getDownloadUrl: (id: string, disposition: FileDisposition = 'attachment') =>
    api.get(`/documents/${id}/download`, { params: { disposition } }).then(r => r.data.data as DocumentDownloadUrl),
  archive: (id: string) =>
    api.patch(`/documents/${id}/archive`).then(r => r.data.data),
  // Suppression définitive (auteur uniquement) — document + versions antérieures
  delete: (id: string) =>
    api.delete(`/documents/${id}`).then(r => r.data.data),

  // ── Fiche document ──
  getDocument: (id: string) =>
    api.get(`/documents/${id}`).then(r => r.data.data as Document),
  getVersions: (id: string) =>
    api.get(`/documents/${id}/versions`).then(r => r.data.data as DocumentVersion[]),
  update: (id: string, data: { name?: string; description?: string; tags?: string[]; folderId?: string }) =>
    api.patch(`/documents/${id}`, data).then(r => r.data.data),

  // ── Commentaires ──
  getComments: (id: string) =>
    api.get(`/documents/${id}/comments`).then(r => r.data.data as DocumentComment[]),
  addComment: (id: string, data: { content: string; pageNumber?: number }) =>
    api.post(`/documents/${id}/comments`, data).then(r => r.data.data),
  resolveComment: (id: string, commentId: string) =>
    api.patch(`/documents/${id}/comments/${commentId}/resolve`).then(r => r.data.data),
  deleteComment: (id: string, commentId: string) =>
    api.delete(`/documents/${id}/comments/${commentId}`).then(r => r.data.data),

  // ── Dossiers ──
  deleteFolder: (id: string) =>
    api.delete(`/documents/folders/${id}`).then(r => r.data.data),
}

// ── Meetings ──────────────────────────────────────────────────────────────────
export interface Meeting {
  id: string; title: string; description?: string
  type: string; status: string
  startDate: string; endDate: string; startTime?: string; endTime?: string; location?: string
  notes?: string
  organizer: { id: string; firstName: string; lastName: string }
  participants: { id: string; userId: string; status: string; user: { id: string; firstName: string; lastName: string; avatarUrl?: string; email?: string } }[]
  actions?: { id: string; description: string; status: string; dueDate?: string; assignee?: { id: string; firstName: string; lastName: string } }[]
  _count?: { agendaItems: number; actions: number }
  createdAt: string
}

export interface MeetingAction {
  id: string; title: string; description?: string; status: string; dueDate?: string
  assignee?: { id: string; firstName: string; lastName: string }
}

export const meetingsApi = {
  list: (params?: { search?: string; status?: string; page?: number }) =>
    api.get('/meetings', { params }).then(r => r.data.data),
  get: (id: string) =>
    api.get(`/meetings/${id}`).then(r => r.data.data),
  create: (data: { title: string; type?: string; startDate: string; endDate: string; description?: string; location?: string }) =>
    api.post('/meetings', data).then(r => r.data.data),
  update: (id: string, data: any) =>
    api.patch(`/meetings/${id}`, data).then(r => r.data.data),
  start: (id: string) =>
    api.post(`/meetings/${id}/start`).then(r => r.data.data),
  complete: (id: string, notes?: string) =>
    api.post(`/meetings/${id}/complete`, { notes }).then(r => r.data.data),
  cancel: (id: string, reason?: string) =>
    api.post(`/meetings/${id}/cancel`, { reason }).then(r => r.data.data),
  addParticipant: (id: string, userId: string) =>
    api.post(`/meetings/${id}/participants`, { userId }).then(r => r.data.data),
  getActions: (id: string) =>
    api.get(`/meetings/${id}/actions`).then(r => r.data.data),
  addAction: (id: string, data: { title: string; assigneeId?: string; dueDate?: string }) =>
    api.post(`/meetings/${id}/actions`, data).then(r => r.data.data),
  updateAction: (id: string, actionId: string, data: any) =>
    api.patch(`/meetings/${id}/actions/${actionId}`, data).then(r => r.data.data),
  updateNotes: (id: string, notes: string) =>
    api.patch(`/meetings/${id}/notes`, { notes }).then(r => r.data.data),
  rsvp: (id: string, status: 'ACCEPTED' | 'DECLINED') =>
    api.patch(`/meetings/${id}/rsvp`, { status }).then(r => r.data.data),

  // Actions supplémentaires
  removeParticipant: (id: string, participantId: string) =>
    api.delete(`/meetings/${id}/participants/${participantId}`).then(r => r.data.data),

  deleteAction: (id: string, actionId: string) =>
    api.delete(`/meetings/${id}/actions/${actionId}`).then(r => r.data.data),

  getAgenda: (id: string) =>
    api.get(`/meetings/${id}/agenda`).then(r => r.data.data),

  addAgendaItem: (id: string, data: { title: string; duration?: number; order?: number }) =>
    api.post(`/meetings/${id}/agenda`, data).then(r => r.data.data),

  deleteAgendaItem: (id: string, itemId: string) =>
    api.delete(`/meetings/${id}/agenda/${itemId}`).then(r => r.data.data),

  getUpcoming: (limit?: number) =>
    api.get('/meetings/upcoming', { params: { limit } }).then(r => r.data.data),

  getMeetingStats: () =>
    api.get('/meetings/stats').then(r => r.data.data),
}

// ── Notes ─────────────────────────────────────────────────────────────────────
export interface Note {
  id: string; title: string; plainText?: string; content?: any
  isPinned: boolean; isArchived: boolean; tags: string[]; color?: string
  author: { id: string; firstName: string; lastName: string }
  lastEditedBy?: { id: string; firstName: string; lastName: string }
  folder?: { id: string; name: string }
  createdAt: string; updatedAt: string
}

export const notesApi = {
  list: (params?: { search?: string; folderId?: string; isPinned?: boolean; myNotes?: boolean; page?: number }) =>
    api.get('/notes', { params }).then(r => r.data.data),
  get: (id: string) =>
    api.get(`/notes/${id}`).then(r => r.data.data),
  create: (data: { title: string; folderId?: string; content?: any; tags?: string[]; color?: string }) =>
    api.post('/notes', data).then(r => r.data.data),
  update: (id: string, data: any) =>
    api.patch(`/notes/${id}`, data).then(r => r.data.data),
  autoSave: (id: string, data: { content: any; plainText?: string }) =>
    api.patch(`/notes/${id}/autosave`, data).then(r => r.data.data),
  pin: (id: string) =>
    api.patch(`/notes/${id}/pin`).then(r => r.data.data),
  archive: (id: string) =>
    api.patch(`/notes/${id}/archive`).then(r => r.data.data),
  delete: (id: string) =>
    api.delete(`/notes/${id}`).then(r => r.data.data),
  getRecent: (limit?: number) =>
    api.get('/notes/recent', { params: { limit } }).then(r => r.data.data),
  getStats: () =>
    api.get('/notes/stats').then(r => r.data.data),
  getArchived: () =>
    api.get('/notes/archived').then(r => r.data.data),
  restore: (id: string) =>
    api.patch(`/notes/${id}/restore`).then(r => r.data.data),
  move: (id: string, folderId: string) =>
    api.patch(`/notes/${id}/move`, { folderId }).then(r => r.data.data),
}
